/**
 * Created by WorkAxis www.workaxis.in
 * Developer: Padma Dev.E
 * Developer Contact: padmadev.e@workaxis.in
 * Admin: shyam@workaxis.in
 * Date: 3/13/2022
 * Time: 9:52 AM
 * Project: w_crm
 */
import React, { useEffect, useMemo } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import PublicLayout from "./PublicLayout";
import PrivateLayout from "./PrivateLayout";
import { useDispatch, useSelector } from "react-redux";
import { authState, checkAuth } from "../redux/auth/authReducer";
import SharedLayout from "./SharedLayout";
import ClientLayout from "./ClientLayout";
import LoadingSpinner from "../component/LoadingSpinner/LoadingSpinner";
import { getProfileApi, profileState } from "../redux/profile/profileReducer";
import { getCookie } from "../utils/cookies";
import { isJwtValid } from "../utils/common";
import { ACCOUNTS_COOKIE_KEY, ACTIVE_ACCOUNT_COOKIE_KEY, isAiFeaturesEnabledForOrg } from "../utils/constants";
import {
    baseRoutes,
    getActiveRouteConfig,
    getConfiguredRoutes,
    resolveBaseHomePath
} from "./routesConfig";
import { useBrand } from "../context/BrandContext";
import { URL_HOME, URL_LOGIN } from "./url";

export const routes = baseRoutes;

export default function UrlRoutes() {
    let dispatch = useDispatch();
    const location = useLocation();
    const { profile } = useSelector(profileState);
    const { isAuthenticated } = useSelector(authState);
    const brand = useBrand();
    const profileRouteConfig = profile?.data?.route_config;
    const hasAuthenticatedSession = useMemo(() => {
        const activeId = getCookie(ACTIVE_ACCOUNT_COOKIE_KEY);

        if (!activeId) {
            return false;
        }

        try {
            const accounts = JSON.parse(getCookie(ACCOUNTS_COOKIE_KEY) || "[]");
            const activeAccount = accounts.find((account) => account.id.toString() === activeId.toString());

            return Boolean(activeAccount?.access && isJwtValid(activeAccount.access));
        } catch (error) {
            return false;
        }
    }, [isAuthenticated]);
    const shouldUseAuthenticatedRouting = isAuthenticated || hasAuthenticatedSession;

    const activeRouteConfig = useMemo(() => {
        return getActiveRouteConfig({
            isAuthenticated: shouldUseAuthenticatedRouting,
            profileRouteConfig,
            brandRouteConfig: brand?.route_config,
        });
    }, [brand?.route_config, profileRouteConfig, shouldUseAuthenticatedRouting]);

    const configuredRoutes = useMemo(() => {
        return getConfiguredRoutes(activeRouteConfig);
    }, [activeRouteConfig]);

    const homePath = useMemo(() => {
        if (shouldUseAuthenticatedRouting) {
            if (!profile?.data) {
                return null;
            }

            return resolveBaseHomePath(activeRouteConfig, configuredRoutes);
        }

        return resolveBaseHomePath(activeRouteConfig, configuredRoutes);
    }, [activeRouteConfig, configuredRoutes, profile?.data, shouldUseAuthenticatedRouting]);

    // Safe destination for "/". Never redirect to "/" itself — that loops forever.
    const rootRedirectTo = useMemo(() => {
        if (shouldUseAuthenticatedRouting) {
            if (!homePath || homePath === "/") {
                return URL_HOME;
            }
            return homePath;
        }

        // Logged-out visitors always land on login, not a private home route.
        return URL_LOGIN;
    }, [homePath, shouldUseAuthenticatedRouting]);

    useEffect(() => {
        dispatch(checkAuth());
    }, [dispatch]);

    useEffect(() => {
        if (shouldUseAuthenticatedRouting && location.pathname === "/" && !profile?.data && !profile.isLoading) {
            dispatch(getProfileApi());
        }
    }, [dispatch, location.pathname, profile?.data, profile.isLoading, shouldUseAuthenticatedRouting]);

    const waitingForProfile = shouldUseAuthenticatedRouting && !profile?.data;
    const rootRouteElement = waitingForProfile
        ? <LoadingSpinner fill="full-screen" isLoading={true} text="Loading workspace..." />
        : <Navigate to={rootRedirectTo} replace />;

    return (
        <Routes>
            <Route path="/" element={rootRouteElement} />
            {configuredRoutes.public.map((route) => {
                const { component, path } = route;
                return (
                    <Route
                        key={path}
                        path={path}
                        element={<PublicLayout component={component} />}
                    />
                );
            })}

            {configuredRoutes.private.map((route) => {
                const { component, path, configuration, notAllowed, id } = route;
                const roleName = profile.data?.userorgrolemapping?.role?.name;
                const orgId = profile?.data?.userorgrolemapping?.organization?.id;
                const isOrgBlocked = ["pricing", "subscription", "credits"].includes(id) && orgId === "f0c690e6-fc36-4380-9971-3d5fb5df77d0";
                // AI Agent + AI Campaign routes (org helper always true; role/plan via ComponentWrapper)
                const isAiRouteBlocked =
                    ["ai_agent", "ai_campaigns", "ai_campaign_new", "ai_campaign_detail"].includes(id) &&
                    !isAiFeaturesEnabledForOrg(orgId);

                if (notAllowed.includes(roleName) || isOrgBlocked || isAiRouteBlocked) return null;

                return (
                    <Route
                        key={path}
                        path={path}
                        element={
                            <PrivateLayout
                                component={component}
                                configuration={configuration}
                            />
                        }
                    />
                );
            })}

            {configuredRoutes.shared.map((route) => {
                const { component, path } = route;
                return (
                    <Route
                        key={path}
                        path={path}
                        element={<SharedLayout component={component} />}
                    />
                );
            })}

            {configuredRoutes.client.map((route) => {
                const { component, path } = route;
                return (
                    <Route
                        key={path}
                        path={path}
                        element={<ClientLayout component={component} />}
                    />
                );
            })}
        </Routes>
    );
}
